import { useState, useEffect } from "react";
import { format, differenceInDays, addYears, isBefore } from "date-fns";
import { CalendarIcon, Plus, Trash2, Bell, Gift, Edit2, X, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface GiftReminder {
  id: string;
  event_name: string;
  recipient_name: string | null;
  event_date: string;
  message: string | null;
  reminder_days_before: number;
  is_recurring: boolean;
}

interface ImportantDatesManagerProps {
  userId: string;
  compact?: boolean;
}

export function ImportantDatesManager({ userId, compact = false }: ImportantDatesManagerProps) {
  const [reminders, setReminders] = useState<GiftReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Form state
  const [date, setDate] = useState<Date | undefined>();
  const [eventName, setEventName] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [reminderDays, setReminderDays] = useState("7");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchReminders();
  }, [userId]);

  const fetchReminders = async () => {
    try {
      const { data, error } = await supabase
        .from("gift_reminders")
        .select("*")
        .eq("user_id", userId)
        .order("event_date", { ascending: true });

      if (error) throw error;
      setReminders(data || []);
    } catch (error) {
      console.error("Error fetching reminders:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDate(undefined);
    setEventName("");
    setRecipientName("");
    setMessage("");
    setReminderDays("7");
    setEditingId(null);
  };

  const openEditDialog = (reminder: GiftReminder) => {
    setEditingId(reminder.id);
    setDate(new Date(reminder.event_date));
    setEventName(reminder.event_name);
    setRecipientName(reminder.recipient_name || "");
    setMessage(reminder.message || "");
    setReminderDays(reminder.reminder_days_before.toString());
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!date) {
      toast.error("Please select a date");
      return;
    }
    if (!eventName.trim()) {
      toast.error("Please enter an event name");
      return;
    }

    setSaving(true);

    try {
      const reminderData = {
        user_id: userId,
        event_name: eventName.trim(),
        recipient_name: recipientName.trim() || null,
        event_date: format(date, "yyyy-MM-dd"),
        message: message.trim() || null,
        reminder_days_before: parseInt(reminderDays),
        is_recurring: true,
      };

      if (editingId) {
        const { error } = await supabase
          .from("gift_reminders")
          .update(reminderData)
          .eq("id", editingId);

        if (error) throw error;
        toast.success("Reminder updated!");
      } else {
        const { error } = await supabase
          .from("gift_reminders")
          .insert(reminderData);

        if (error) throw error;
        toast.success("Reminder added!");
      }

      setDialogOpen(false);
      resetForm();
      fetchReminders();
    } catch (error) {
      console.error("Error saving reminder:", error);
      toast.error("Failed to save reminder");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("gift_reminders")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("Reminder deleted");
      setReminders(reminders.filter(r => r.id !== id));
    } catch (error) {
      console.error("Error deleting reminder:", error);
      toast.error("Failed to delete reminder");
    }
  };

  const getNextOccurrence = (dateStr: string) => {
    const eventDate = new Date(dateStr);
    const today = new Date();
    
    // Set to this year
    let nextDate = new Date(today.getFullYear(), eventDate.getMonth(), eventDate.getDate());
    
    // If it's already passed this year, move to next year
    if (isBefore(nextDate, today)) {
      nextDate = addYears(nextDate, 1);
    }
    
    return nextDate;
  };

  const getDaysUntil = (dateStr: string) => {
    const nextDate = getNextOccurrence(dateStr);
    return differenceInDays(nextDate, new Date());
  };

  const getStatusBadge = (dateStr: string) => {
    const daysUntil = getDaysUntil(dateStr);
    
    if (daysUntil <= 7) {
      return <Badge variant="destructive">In {daysUntil} days</Badge>;
    } else if (daysUntil <= 30) {
      return <Badge className="bg-amber-500 hover:bg-amber-600">In {daysUntil} days</Badge>;
    } else {
      return <Badge variant="secondary">In {daysUntil} days</Badge>;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/3"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              Important Dates
            </CardTitle>
            <CardDescription>
              Never miss a gift-giving occasion
            </CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-1" />
                Add Date
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit Reminder" : "Add Important Date"}</DialogTitle>
                <DialogDescription>
                  We'll remind you before the event so you have time to organize a group gift.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="eventName">Event Name *</Label>
                  <Input
                    id="eventName"
                    placeholder="e.g., John's Birthday, Wedding Anniversary"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="recipientName">Recipient Name</Label>
                  <Input
                    id="recipientName"
                    placeholder="e.g., John, Mom & Dad"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Event Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Remind Me</Label>
                  <Select value={reminderDays} onValueChange={setReminderDays}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select reminder timing" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days before</SelectItem>
                      <SelectItem value="14">14 days before</SelectItem>
                      <SelectItem value="30">30 days before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Personal Note (optional)</Label>
                  <Textarea
                    id="message"
                    placeholder="Gift ideas, preferences, notes..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Add Reminder"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {reminders.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Gift className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No important dates yet</p>
            <p className="text-sm">Add birthdays, anniversaries, and other occasions to get reminded before they arrive.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reminders.map((reminder) => (
              <div
                key={reminder.id}
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg group hover:bg-muted transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{reminder.event_name}</span>
                    {getStatusBadge(reminder.event_date)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                    <CalendarIcon className="h-3 w-3" />
                    <span>{format(new Date(reminder.event_date), "MMMM d")}</span>
                    {reminder.recipient_name && (
                      <>
                        <span>•</span>
                        <span>For {reminder.recipient_name}</span>
                      </>
                    )}
                  </div>
                  {reminder.message && !compact && (
                    <p className="text-sm text-muted-foreground mt-1 italic truncate">
                      "{reminder.message}"
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => openEditDialog(reminder)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => handleDelete(reminder.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}